import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, BookOpen, ArrowRight, ShieldCheck, Sparkles, Loader2, Mail } from 'lucide-react';
import { ReferralCard } from '../components/ReferralCard';
import useConversionTracker from '../hooks/useConversionTracker';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtEUR = (n, currency = 'EUR') => {
  try {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(Number(n || 0));
  } catch {
    return `${n} ${currency}`;
  }
};

const DankePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id') || '';
  const emailParam = searchParams.get('email') || '';
  const [refEmail, setRefEmail] = useState(emailParam);

  // Feuert Google Ads / GA4 / Meta-Pixel Purchase-Events (idempotent)
  const { data: conversion, status: convStatus } = useConversionTracker({
    orderId,
    email: emailParam });

  // Nach dem Checkout: Konto-Anlage per IPN abwarten → Willkommen/Passwort setzen
  const [onboarding, setOnboarding] = useState(orderId && emailParam ? 'waiting' : 'none');
  const [setupToken, setSetupToken] = useState('');

  useEffect(() => {
    if (onboarding !== 'waiting') return;
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const qs = new URLSearchParams({ order_id: orderId, email: emailParam });
        const res = await fetch(`${API}/api/digistore/onboarding-status?${qs}`);
        const data = await res.json();
        if (data.status === 'ready') { setSetupToken(data.setup_token); setOnboarding('ready'); return; }
        if (data.status === 'existing') { setOnboarding('existing'); return; }
      } catch { /* weiter pollen */ }
      if (attempts >= 20) { setOnboarding('timeout'); return; }
      timer = setTimeout(poll, 3000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [onboarding, orderId, emailParam]);

  // Weiterleitung erst, nachdem die Conversion gefeuert wurde
  useEffect(() => {
    if (onboarding !== 'ready' || convStatus === 'idle' || convStatus === 'loading') return;
    const t = setTimeout(() => navigate(`/setup-password?token=${encodeURIComponent(setupToken)}&welcome=1`), 1200);
    return () => clearTimeout(t);
  }, [onboarding, convStatus, setupToken, navigate]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('company');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.email) setRefEmail(parsed.email);
      }
    } catch (err) {
      // Corrupted localStorage → wir zeigen halt keinen vor-ausgefüllten Referral-Wert
      console.warn('DankePage: company aus localStorage nicht parsebar', err);
    }
  }, [refEmail]);

  const amountLine = conversion?.amount > 0
    ? fmtEUR(conversion.amount, conversion.currency)
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-black flex items-center justify-center p-4">
      <Helmet>
        <title>Vielen Dank - SmartVolt Academy</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-lg w-full space-y-5" data-testid="danke-page-wrapper">
        <Card className="bg-gray-900/50 border-gray-800" data-testid="danke-page">
          <CardContent className="pt-10 pb-8 text-center space-y-6">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>

            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Vielen Dank!</h1>
              <p className="text-gray-400">
                Ihr 14-Tage-Testzugang ist gebucht. Heute wurden 0,00 € abgebucht.
              </p>
            </div>

            {(onboarding === 'waiting' || onboarding === 'ready') && (
              <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/5 p-4 flex items-center gap-3 text-left" data-testid="danke-onboarding-waiting">
                <Loader2 className="w-5 h-5 text-yellow-400 animate-spin shrink-0" />
                <p className="text-sm text-gray-200">
                  {onboarding === 'ready'
                    ? 'Ihr Konto ist bereit – Sie werden zum Passwort-Setzen weitergeleitet…'
                    : 'Ihr Firmenkonto wird gerade eingerichtet. Einen Moment bitte…'}
                </p>
              </div>
            )}
            {onboarding === 'timeout' && (
              <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-4 flex items-start gap-3 text-left" data-testid="danke-onboarding-email-hint">
                <Mail className="w-5 h-5 text-blue-300 shrink-0 mt-0.5" />
                <p className="text-sm text-gray-200">
                  Die Einrichtung dauert etwas länger. Sie erhalten in wenigen Minuten eine E-Mail an <strong>{emailParam}</strong> mit Ihrem persönlichen Link zum Passwort-Setzen.
                </p>
              </div>
            )}

            {/* Order-Summary: zeigt den echten Verkaufsbetrag */}
            {(orderId || amountLine) && (
              <div
                className="rounded-xl border border-yellow-500/20 bg-yellow-500/5 p-4 text-left space-y-1"
                data-testid="danke-order-summary"
              >
                {amountLine && (
                  <p className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Bezahlter Betrag</span>
                    <span className="text-white font-semibold" data-testid="danke-order-amount">
                      {amountLine}
                    </span>
                  </p>
                )}
                {conversion?.quantity > 1 && (
                  <p className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Lizenzen</span>
                    <span className="text-white font-semibold">{conversion.quantity} Seats</span>
                  </p>
                )}
                {orderId && (
                  <p className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Bestell-Nr.</span>
                    <span className="text-gray-400 font-mono" data-testid="danke-order-id">{orderId}</span>
                  </p>
                )}
              </div>
            )}

            {onboarding !== 'waiting' && onboarding !== 'ready' && (
            <div className="space-y-3 pt-2">
              <Button
                onClick={() => navigate('/kurse')}
                className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-bold py-3"
                data-testid="danke-to-kurse-btn"
              >
                <BookOpen className="w-4 h-4 mr-2" /> Direkt zu deinen Kursen — 1-Klick-Start
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="w-full border-yellow-500/30 text-yellow-300 hover:bg-yellow-500/10"
                data-testid="danke-to-dashboard-btn"
              >
                Zum Chef-Dashboard (Mitarbeiter zuweisen) <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                onClick={() => navigate('/login')}
                variant="ghost"
                className="w-full text-gray-400 hover:text-white"
                data-testid="danke-to-login-btn"
              >
                Anmelden <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            )}

            <p className="text-xs text-gray-600">
              Sie erhalten zusätzlich eine Bestätigungs-E-Mail mit Ihrem Zugangslink.
            </p>

            {/* Digistore24-Rechtshinweis (verpflichtend für Zahlungsabwickler-Transparenz) */}
            <div
              className="mt-2 pt-3 border-t border-gray-800 flex items-center justify-center gap-2 text-[11px] text-gray-500"
              data-testid="danke-digistore-notice"
            >
              <ShieldCheck className="w-3.5 h-3.5 text-yellow-500 shrink-0" />
              <span>
                Die Abbuchung erfolgt durch{' '}
                <a
                  href="https://www.digistore24.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-yellow-400 hover:underline"
                >
                  Digistore24.com
                </a>
              </span>
            </div>

            {/* Dezenter Tracking-Status (nur informativ, kein Pflicht-Hinweis) */}
            {convStatus === 'fired' && (
              <p
                className="text-[11px] text-gray-600 flex items-center justify-center gap-1"
                data-testid="danke-conversion-fired"
              >
                <ShieldCheck className="w-3 h-3" /> Bestätigung erfolgreich übermittelt.
              </p>
            )}
            {convStatus === 'skipped' && conversion && (
              <p
                className="text-[11px] text-gray-600 flex items-center justify-center gap-1"
                data-testid="danke-conversion-skipped"
              >
                <Sparkles className="w-3 h-3" /> Bestellung registriert – ohne Marketing-Cookies.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Empfehlungs-Block - 50€ Gutschein */}
        <ReferralCard refEmail={refEmail} />
      </div>
    </div>
  );
};

export default DankePage;
